import React, { useState, useEffect, useCallback, useRef } from 'react';
import client from '../api/client';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../context/ToastContext';
import { usePermissions, PAGE_PERMISSION_IDS } from '../hooks/usePermissions';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import SyncedHorizontalScroll from '../components/SyncedHorizontalScroll';
import { idsInOrder, moveItemInArray, swapAdjacentInArray } from '../utils/processOrderUtils';

const ProcessManagement = () => {
  const { isDark } = useTheme();
  const { showError, showSuccess } = useToast();
  const { canEdit } = usePermissions();
  const processCanEdit = canEdit(PAGE_PERMISSION_IDS.process);
  const [roles, setRoles] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  // { id, field: 'number' | 'name' | 'predecessor' } | null
  const [editingStage, setEditingStage] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [showAddStage, setShowAddStage] = useState(false);
  const [showAddRole, setShowAddRole] = useState(false);
  const [newStage, setNewStage] = useState({ number: '', name: '', predecessor_number: '' });
  const [newRole, setNewRole] = useState({ name: '', short_name: '' });
  const [dragRoleIndex, setDragRoleIndex] = useState(null);
  const [dragOverRoleIndex, setDragOverRoleIndex] = useState(null);
  const [dragStageIndex, setDragStageIndex] = useState(null);
  const [dragOverStageIndex, setDragOverStageIndex] = useState(null);
  const dragRoleIndexRef = useRef(null);
  const dragStageIndexRef = useRef(null);

  const isEditingStageField = (stageId, field) => (
    editingStage?.id === stageId && editingStage?.field === field
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [rolesRes, stagesRes] = await Promise.all([
        client.get('/process-management/roles'),
        client.get('/process-management/stages')
      ]);
      setRoles(rolesRes.data);
      setStages(stagesRes.data);
    } catch (error) {
      // Если 404 — данных ещё нет, инициализируем
      if (error.response?.status === 404) {
        try {
          await client.post('/process-management/init-default-data');
          const [rolesRes, stagesRes] = await Promise.all([
            client.get('/process-management/roles'),
            client.get('/process-management/stages')
          ]);
          setRoles(rolesRes.data);
          setStages(stagesRes.data);
        } catch (initError) {
          showError(getApiErrorMessage(initError, 'Ошибка инициализации данных'));
        }
      } else {
        showError(getApiErrorMessage(error, 'Ошибка загрузки данных'));
      }
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!showAddStage && !showAddRole) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowAddStage(false);
        setShowAddRole(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showAddStage, showAddRole]);

  // Получить назначение для этапа и роли
  const getAssignment = (stageId, roleId) => {
    const stage = stages.find(s => s.id === stageId);
    if (!stage || !stage.assignments) return null;
    return stage.assignments.find(a => a.role_id === roleId);
  };

  // Обработка клика по ячейке
  const handleCellClick = async (stageId, roleId) => {
    if (!processCanEdit) return;
    const current = getAssignment(stageId, roleId);
    let newType = null;

    if (!current) {
      newType = 'approver'; // Согласующий
    } else if (current.assignment_type === 'approver') {
      newType = 'responsible'; // Ответственный
    } else {
      try {
        await client.delete(`/process-management/assignments/${stageId}/${roleId}`);
        fetchData();
      } catch (error) {
        showError(getApiErrorMessage(error, 'Ошибка удаления назначения'));
      }
      return;
    }

    try {
      await client.post('/process-management/assignments', {
        stage_id: stageId,
        role_id: roleId,
        assignment_type: newType
      });
      fetchData();
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка сохранения назначения'));
    }
  };

  // Добавление этапа
  const handleAddStage = async () => {
    if (!newStage.number?.trim() || !newStage.name?.trim()) {
      showError('Укажите номер и название этапа');
      return;
    }
    try {
      await client.post('/process-management/stages', newStage);
      setNewStage({ number: '', name: '', predecessor_number: '' });
      setShowAddStage(false);
      fetchData();
      showSuccess('Этап добавлен');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка добавления этапа'));
    }
  };

  // Обновление этапа
  const handleUpdateStage = async (stageId, data) => {
    try {
      await client.put(`/process-management/stages/${stageId}`, data);
      setEditingStage(null);
      fetchData();
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка обновления этапа'));
    }
  };

  // Удаление этапа
  const handleDeleteStage = async (stageId) => {
    if (!window.confirm('Удалить этот этап?')) return;
    try {
      await client.delete(`/process-management/stages/${stageId}`);
      fetchData();
      showSuccess('Этап удалён');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка удаления этапа'));
    }
  };

  // Добавление роли
  const handleAddRole = async () => {
    if (!newRole.name?.trim()) {
      showError('Укажите название роли');
      return;
    }
    try {
      await client.post('/process-management/roles', {
        ...newRole,
        order_index: roles.length
      });
      setNewRole({ name: '', short_name: '' });
      setShowAddRole(false);
      fetchData();
      showSuccess('Роль добавлена');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка добавления роли'));
    }
  };

  // Обновление роли
  const handleUpdateRole = async (roleId, data) => {
    try {
      await client.put(`/process-management/roles/${roleId}`, data);
      setEditingRole(null);
      fetchData();
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка обновления роли'));
    }
  };

  // Удаление роли
  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Удалить эту роль?')) return;
    try {
      await client.delete(`/process-management/roles/${roleId}`);
      fetchData();
      showSuccess('Роль удалена');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка удаления роли'));
    }
  };

  // Сброс и загрузка данных по умолчанию
  const handleResetData = async () => {
    if (!window.confirm('Сбросить все данные и загрузить этапы по умолчанию?')) return;
    try {
      setLoading(true);
      await client.post('/process-management/reset-data');
      fetchData();
      showSuccess('Данные загружены по умолчанию');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка сброса данных'));
    }
  };

  const persistRolesOrder = async (nextRoles) => {
    const previous = roles;
    setRoles(nextRoles);
    try {
      await client.put('/process-management/roles/reorder', idsInOrder(nextRoles));
    } catch (error) {
      setRoles(previous);
      showError(getApiErrorMessage(error, 'Ошибка изменения порядка ролей'));
    }
  };

  const persistStagesOrder = async (nextStages) => {
    const previous = stages;
    setStages(nextStages);
    try {
      await client.put('/process-management/stages/reorder', idsInOrder(nextStages));
    } catch (error) {
      setStages(previous);
      showError(getApiErrorMessage(error, 'Ошибка изменения порядка этапов'));
    }
  };

  const moveRole = async (fromIndex, toIndex) => {
    if (!processCanEdit) return;
    const next = moveItemInArray(roles, fromIndex, toIndex);
    if (next === roles) return;
    await persistRolesOrder(next);
  };

  const moveStage = async (fromIndex, toIndex) => {
    if (!processCanEdit) return;
    const next = moveItemInArray(stages, fromIndex, toIndex);
    if (next === stages) return;
    await persistStagesOrder(next);
  };

  const handleStageMoveUp = (index) => {
    const next = swapAdjacentInArray(stages, index, -1);
    if (next === stages) return;
    persistStagesOrder(next);
  };

  const handleStageMoveDown = (index) => {
    const next = swapAdjacentInArray(stages, index, 1);
    if (next === stages) return;
    persistStagesOrder(next);
  };

  // Стили с поддержкой тёмной темы
  const styles = {
    container: {
      padding: '20px',
      fontFamily: "'Inter', -apple-system, sans-serif",
      maxWidth: '100%',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      flexWrap: 'wrap',
      gap: '10px'
    },
    title: {
      fontSize: '24px',
      fontWeight: '700',
      color: isDark ? '#f8fafc' : '#1e293b',
      margin: 0
    },
    buttonGroup: {
      display: 'flex',
      gap: '10px'
    },
    button: {
      padding: '8px 16px',
      borderRadius: '6px',
      border: 'none',
      cursor: 'pointer',
      fontWeight: '500',
      fontSize: '13px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    primaryButton: {
      background: '#7b9eb8',
      color: '#fff'
    },
    secondaryButton: {
      background: isDark ? '#475569' : '#f1f5f9',
      color: isDark ? '#f8fafc' : '#475569'
    },
    dangerButton: {
      background: '#d4a0a0',
      color: '#fff'
    },
    tableWrapper: {
      border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
      borderRadius: '8px',
      maxHeight: 'calc(100vh - 220px)',
      overflow: 'auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '12px',
      minWidth: '1200px'
    },
    th: {
      background: isDark ? '#334155' : '#f8fafc',
      padding: '10px 8px',
      borderBottom: `2px solid ${isDark ? '#334155' : '#e2e8f0'}`,
      borderRight: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
      fontWeight: '600',
      color: isDark ? '#f8fafc' : '#475569',
      textAlign: 'center',
      position: 'sticky',
      top: 0,
      zIndex: 10,
      minWidth: '80px'
    },
    thNumber: {
      width: '40px',
      minWidth: '40px'
    },
    thStage: {
      minWidth: '300px',
      textAlign: 'left'
    },
    thPredecessor: {
      width: '80px',
      minWidth: '80px'
    },
    thDragOver: {
      outline: '2px dashed #7b9eb8',
      outlineOffset: -2,
    },
    dragHandle: {
      cursor: 'grab',
      fontSize: '11px',
      color: isDark ? '#94a3b8' : '#64748b',
      userSelect: 'none',
      padding: '2px 4px',
      lineHeight: 1,
    },
    orderButtons: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      marginRight: '6px',
    },
    orderButton: {
      padding: '1px 5px',
      fontSize: '10px',
      border: `1px solid ${isDark ? '#475569' : '#d1d5db'}`,
      borderRadius: '3px',
      background: isDark ? '#334155' : '#f8fafc',
      color: isDark ? '#e2e8f0' : '#475569',
      cursor: 'pointer',
      lineHeight: 1.2,
    },
    trDragOver: {
      boxShadow: 'inset 0 2px 0 #7b9eb8',
    },
    td: {
      padding: '8px',
      borderBottom: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
      borderRight: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
      textAlign: 'center',
      verticalAlign: 'middle'
    },
    tdStage: {
      textAlign: 'left',
      maxWidth: '400px'
    },
    cell: {
      minHeight: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      borderRadius: '4px',
      transition: 'all 0.15s ease'
    },
    cellApprover: {
      background: '#fef08a',
      color: '#854d0e',
      fontWeight: '600',
      padding: '4px 8px'
    },
    cellResponsible: {
      background: isDark ? '#475569' : '#fff',
      border: `1px solid ${isDark ? '#64748b' : '#d1d5db'}`,
      color: isDark ? '#f8fafc' : '#374151',
      fontWeight: '500',
      padding: '4px 8px'
    },
    cellEmpty: {
      background: isDark ? '#1e293b' : '#f9fafb',
      minHeight: '32px'
    },
    input: {
      padding: '6px 10px',
      border: `1px solid ${isDark ? '#475569' : '#d1d5db'}`,
      borderRadius: '4px',
      fontSize: '12px',
      width: '100%',
      background: isDark ? '#1e293b' : '#fff',
      color: isDark ? '#f8fafc' : '#1e293b'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modalContent: {
      background: isDark ? '#1e293b' : '#fff',
      padding: '24px',
      borderRadius: '12px',
      minWidth: '400px',
      maxWidth: '500px'
    },
    modalTitle: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '16px',
      color: isDark ? '#f8fafc' : '#1e293b'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontWeight: '500',
      color: isDark ? '#94a3b8' : '#475569',
      fontSize: '13px'
    },
    legend: {
      display: 'flex',
      gap: '20px',
      marginBottom: '16px',
      padding: '12px',
      background: isDark ? '#334155' : '#f8fafc',
      borderRadius: '8px',
      fontSize: '12px',
      color: isDark ? '#f8fafc' : 'inherit'
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    legendBox: {
      width: '60px',
      height: '24px',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '10px',
      fontWeight: '600'
    },
    stageNumber: {
      fontWeight: '600',
      color: isDark ? '#f8fafc' : '#1e293b'
    },
    stageName: {
      color: isDark ? '#94a3b8' : '#475569'
    },
    parentStage: {
      background: isDark ? '#334155' : '#f0f9ff',
      fontWeight: '600',
      color: isDark ? '#e2e8f0' : 'inherit'
    },
    childStage: {
      paddingLeft: '20px'
    },
    actions: {
      display: 'flex',
      gap: '4px',
      marginLeft: '8px'
    },
    actionButton: {
      padding: '4px 8px',
      borderRadius: '4px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '11px',
      background: isDark ? '#475569' : '#f1f5f9',
      color: isDark ? '#94a3b8' : '#64748b'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          Загрузка...
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>📋 Процесс управления циклом девелопмента</h1>
        <div style={styles.buttonGroup}>
          {processCanEdit ? (
            <>
              <button
                style={{ ...styles.button, ...styles.dangerButton }}
                onClick={handleResetData}
                title="Сбросить все данные и загрузить этапы по умолчанию"
              >
                🔄 Загрузить данные
              </button>
              <button
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={() => setShowAddRole(true)}
              >
                ➕ Добавить роль
              </button>
              <button
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={() => setShowAddStage(true)}
                data-testid="process-add-stage"
              >
                ➕ Добавить этап
              </button>
            </>
          ) : (
            <span style={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: '14px' }}>
              Режим просмотра
            </span>
          )}
        </div>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <span style={{ fontWeight: '600', color: isDark ? '#94a3b8' : '#475569' }}>Легенда:</span>
        <div style={styles.legendItem}>
          <div style={{ ...styles.legendBox, ...styles.cellApprover }}>
            Согласующий
          </div>
          <span>— участвует в согласовании</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ ...styles.legendBox, ...styles.cellResponsible }}>
            Ответственный
          </div>
          <span>— ответственный исполнитель</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ color: isDark ? '#94a3b8' : '#64748b' }}>💡 Кликните по ячейке для изменения роли</span>
        </div>
        {processCanEdit && (
          <div style={styles.legendItem}>
            <span style={{ color: isDark ? '#94a3b8' : '#64748b' }}>
              ⠿ Перетаскивайте роли (←→) и этапы (↑↓), чтобы менять порядок
            </span>
          </div>
        )}
      </div>

      {/* Table */}
      <SyncedHorizontalScroll
        data-testid="process-management-table-scroll"
        style={styles.tableWrapper}
        maxHeight="calc(100vh - 220px)"
        contentKey={`${stages.length}-${roles.length}`}
      >
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, ...styles.thNumber }}>№</th>
              <th style={{ ...styles.th, ...styles.thStage }}>Этапы процесса</th>
              <th style={{ ...styles.th, ...styles.thPredecessor }}>Предшеств. задача (№п)</th>
              {roles.map((role, roleIndex) => (
                <th
                  key={role.id}
                  data-testid={`process-role-col-${role.id}`}
                  style={{
                    ...styles.th,
                    ...(dragOverRoleIndex === roleIndex ? styles.thDragOver : {}),
                    opacity: dragRoleIndex === roleIndex ? 0.55 : 1,
                  }}
                  draggable={processCanEdit && editingRole !== role.id}
                  onDragStart={(e) => {
                    if (!processCanEdit || editingRole === role.id) {
                      e.preventDefault();
                      return;
                    }
                    dragRoleIndexRef.current = roleIndex;
                    setDragRoleIndex(roleIndex);
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', String(role.id));
                  }}
                  onDragOver={(e) => {
                    if (!processCanEdit || dragRoleIndexRef.current == null) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    if (dragOverRoleIndex !== roleIndex) setDragOverRoleIndex(roleIndex);
                  }}
                  onDragLeave={() => {
                    if (dragOverRoleIndex === roleIndex) setDragOverRoleIndex(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const from = dragRoleIndexRef.current;
                    setDragRoleIndex(null);
                    setDragOverRoleIndex(null);
                    dragRoleIndexRef.current = null;
                    if (from == null || from === roleIndex) return;
                    moveRole(from, roleIndex);
                  }}
                  onDragEnd={() => {
                    setDragRoleIndex(null);
                    setDragOverRoleIndex(null);
                    dragRoleIndexRef.current = null;
                  }}
                  title={processCanEdit ? 'Перетащите, чтобы изменить порядок ролей' : undefined}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                    {processCanEdit && (
                      <span style={styles.dragHandle} title="Перетащить">⠿</span>
                    )}
                    {editingRole === role.id ? (
                      <input
                        style={{ ...styles.input, width: '80px' }}
                        value={role.name}
                        onChange={(e) => {
                          const updated = roles.map(r =>
                            r.id === role.id ? { ...r, name: e.target.value } : r
                          );
                          setRoles(updated);
                        }}
                        onBlur={() => handleUpdateRole(role.id, { name: role.name })}
                        onKeyDown={(e) => e.key === 'Enter' && handleUpdateRole(role.id, { name: role.name })}
                        autoFocus
                      />
                    ) : (
                      <span
                        onClick={() => processCanEdit && setEditingRole(role.id)}
                        style={{ cursor: processCanEdit ? 'pointer' : 'default' }}
                        title="Кликните для редактирования"
                      >
                        {role.name}
                      </span>
                    )}
                    {processCanEdit && (
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <button
                          type="button"
                          style={styles.orderButton}
                          disabled={roleIndex === 0}
                          onClick={() => moveRole(roleIndex, roleIndex - 1)}
                          title="Влево"
                        >
                          ←
                        </button>
                        <button
                          type="button"
                          style={styles.orderButton}
                          disabled={roleIndex === roles.length - 1}
                          onClick={() => moveRole(roleIndex, roleIndex + 1)}
                          title="Вправо"
                        >
                          →
                        </button>
                        <button
                          style={{ ...styles.actionButton, fontSize: '10px', padding: '2px 6px' }}
                          onClick={() => handleDeleteRole(role.id)}
                          title="Удалить роль"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {stages.map((stage, stageIndex) => {
              const isParent = !stage.number.includes('.');
              const isEditingAnyField = editingStage?.id === stage.id;
              return (
                <tr
                  key={stage.id}
                  data-testid={`process-stage-row-${stage.id}`}
                  style={{
                    ...(isParent ? styles.parentStage : {}),
                    ...(dragOverStageIndex === stageIndex ? styles.trDragOver : {}),
                    opacity: dragStageIndex === stageIndex ? 0.55 : 1,
                  }}
                  draggable={processCanEdit && !isEditingAnyField}
                  onDragStart={(e) => {
                    if (!processCanEdit || isEditingAnyField) {
                      e.preventDefault();
                      return;
                    }
                    // Avoid starting drag from inputs/buttons
                    if (e.target.closest('input,button,textarea,select')) {
                      e.preventDefault();
                      return;
                    }
                    dragStageIndexRef.current = stageIndex;
                    setDragStageIndex(stageIndex);
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', String(stage.id));
                  }}
                  onDragOver={(e) => {
                    if (!processCanEdit || dragStageIndexRef.current == null) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    if (dragOverStageIndex !== stageIndex) setDragOverStageIndex(stageIndex);
                  }}
                  onDragLeave={() => {
                    if (dragOverStageIndex === stageIndex) setDragOverStageIndex(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const from = dragStageIndexRef.current;
                    setDragStageIndex(null);
                    setDragOverStageIndex(null);
                    dragStageIndexRef.current = null;
                    if (from == null || from === stageIndex) return;
                    moveStage(from, stageIndex);
                  }}
                  onDragEnd={() => {
                    setDragStageIndex(null);
                    setDragOverStageIndex(null);
                    dragStageIndexRef.current = null;
                  }}
                >
                  <td style={styles.td}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                      {processCanEdit && (
                        <span style={styles.dragHandle} title="Перетащить этап">⠿</span>
                      )}
                      {isEditingStageField(stage.id, 'number') ? (
                        <input
                          style={{ ...styles.input, width: '40px' }}
                          value={stage.number}
                          onChange={(e) => {
                            const updated = stages.map(s =>
                              s.id === stage.id ? { ...s, number: e.target.value } : s
                            );
                            setStages(updated);
                          }}
                          onBlur={() => handleUpdateStage(stage.id, { number: stage.number })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleUpdateStage(stage.id, { number: stage.number });
                            if (e.key === 'Escape') setEditingStage(null);
                          }}
                          autoFocus
                        />
                      ) : (
                        <span
                          style={{ ...styles.stageNumber, cursor: processCanEdit ? 'pointer' : 'default' }}
                          onClick={() => processCanEdit && setEditingStage({ id: stage.id, field: 'number' })}
                          title={processCanEdit ? 'Кликните для редактирования' : undefined}
                        >
                          {stage.number}
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ ...styles.td, ...styles.tdStage }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                        {processCanEdit && (
                          <div style={styles.orderButtons}>
                            <button
                              type="button"
                              style={styles.orderButton}
                              disabled={stageIndex === 0}
                              onClick={() => handleStageMoveUp(stageIndex)}
                              title="Вверх"
                              data-testid={`process-stage-move-up-${stage.id}`}
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              style={styles.orderButton}
                              disabled={stageIndex === stages.length - 1}
                              onClick={() => handleStageMoveDown(stageIndex)}
                              title="Вниз"
                              data-testid={`process-stage-move-down-${stage.id}`}
                            >
                              ↓
                            </button>
                          </div>
                        )}
                        <div style={!isParent ? styles.childStage : {}}>
                          {isEditingStageField(stage.id, 'name') ? (
                            <input
                              style={{ ...styles.input, minWidth: '250px' }}
                              value={stage.name}
                              onChange={(e) => {
                                const updated = stages.map(s =>
                                  s.id === stage.id ? { ...s, name: e.target.value } : s
                                );
                                setStages(updated);
                              }}
                              onBlur={() => handleUpdateStage(stage.id, { name: stage.name })}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleUpdateStage(stage.id, { name: stage.name });
                                if (e.key === 'Escape') setEditingStage(null);
                              }}
                              autoFocus
                            />
                          ) : (
                            <span
                              style={{
                                ...(isParent ? { fontWeight: '600' } : styles.stageName),
                                cursor: processCanEdit ? 'pointer' : 'default',
                              }}
                              onClick={() => processCanEdit && setEditingStage({ id: stage.id, field: 'name' })}
                              title={processCanEdit ? 'Кликните для редактирования' : undefined}
                            >
                              {stage.name}
                            </span>
                          )}
                        </div>
                      </div>
                      {processCanEdit && (
                        <div style={styles.actions}>
                          <button
                            style={styles.actionButton}
                            onClick={() => handleDeleteStage(stage.id)}
                            title="Удалить этап"
                          >
                            🗑️
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={styles.td}>
                    {isEditingStageField(stage.id, 'predecessor') ? (
                      <input
                        style={{ ...styles.input, width: '60px' }}
                        value={stage.predecessor_number || ''}
                        onChange={(e) => {
                          const updated = stages.map(s =>
                            s.id === stage.id ? { ...s, predecessor_number: e.target.value } : s
                          );
                          setStages(updated);
                        }}
                        onBlur={() => handleUpdateStage(stage.id, { predecessor_number: stage.predecessor_number })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleUpdateStage(stage.id, { predecessor_number: stage.predecessor_number });
                          }
                          if (e.key === 'Escape') setEditingStage(null);
                        }}
                        autoFocus
                      />
                    ) : (
                      <span
                        onClick={() => processCanEdit && setEditingStage({ id: stage.id, field: 'predecessor' })}
                        style={{ cursor: processCanEdit ? 'pointer' : 'default' }}
                        title={processCanEdit ? 'Кликните для редактирования предшественника' : undefined}
                      >
                        {stage.predecessor_number || '-'}
                      </span>
                    )}
                  </td>
                  {roles.map(role => {
                    const assignment = getAssignment(stage.id, role.id);
                    return (
                      <td key={role.id} style={styles.td}>
                        <div
                          style={{
                            ...styles.cell,
                            ...(assignment?.assignment_type === 'approver'
                              ? styles.cellApprover
                              : assignment?.assignment_type === 'responsible'
                              ? styles.cellResponsible
                              : styles.cellEmpty),
                            cursor: processCanEdit ? 'pointer' : 'default',
                          }}
                          onClick={() => processCanEdit && handleCellClick(stage.id, role.id)}
                          title={
                            !processCanEdit ? 'Только просмотр' :
                            assignment?.assignment_type === 'approver'
                              ? 'Согласующий (клик = Ответственный)'
                              : assignment?.assignment_type === 'responsible'
                              ? 'Ответственный (клик = убрать)'
                              : 'Пусто (клик = Согласующий)'
                          }
                        >
                          {assignment?.assignment_type === 'approver' && 'Согласующий'}
                          {assignment?.assignment_type === 'responsible' && 'Ответственный'}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </SyncedHorizontalScroll>

      {/* Modal: Add Stage */}
      {showAddStage && (
        <div style={styles.modal} onClick={() => setShowAddStage(false)} data-testid="process-add-stage-modal">
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Добавить этап</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Номер этапа *</label>
              <input
                style={styles.input}
                placeholder="Например: 1.9 или 3"
                value={newStage.number}
                onChange={(e) => setNewStage({ ...newStage, number: e.target.value })}
                data-testid="process-add-stage-number"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Название этапа *</label>
              <input
                style={styles.input}
                placeholder="Название этапа процесса"
                value={newStage.name}
                onChange={(e) => setNewStage({ ...newStage, name: e.target.value })}
                data-testid="process-add-stage-name"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Предшествующая задача (№п)</label>
              <input
                style={styles.input}
                placeholder="Например: 1.8"
                value={newStage.predecessor_number}
                onChange={(e) => setNewStage({ ...newStage, predecessor_number: e.target.value })}
                data-testid="process-add-stage-predecessor"
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={() => setShowAddStage(false)}
              >
                Отмена
              </button>
              <button
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={handleAddStage}
                data-testid="process-add-stage-submit"
              >
                Добавить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Add Role */}
      {showAddRole && (
        <div style={styles.modal} onClick={() => setShowAddRole(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Добавить роль/должность</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Название должности *</label>
              <input
                style={styles.input}
                placeholder="Например: Юрист"
                value={newRole.name}
                onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Сокращённое название</label>
              <input
                style={styles.input}
                placeholder="Например: ЮР"
                value={newRole.short_name}
                onChange={(e) => setNewRole({ ...newRole, short_name: e.target.value })}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={() => setShowAddRole(false)}
              >
                Отмена
              </button>
              <button
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={handleAddRole}
              >
                Добавить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessManagement;
